import { Avatar, Box, Chip, Link, Typography } from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { STATUS_META, blogPostUrl } from "./Blog.constants";

// Byline: Nirva for admin-authored posts, else the practitioner's name/avatar.
const authorName = (post) => {
  if (post.authorType === "nirva") return "Nirva";
  const a = post.author;
  // `/blog/mine` returns no author join — that list is always the reader's own.
  return a ? `${a.firstName || ""} ${a.lastName || ""}`.trim() || "You" : "You";
};

const postDate = (post) => {
  const d = post.publishedAt || post.createdAt;
  if (!d) return "";
  try {
    return new Date(d).toISOString().slice(0, 10);
  } catch {
    return "";
  }
};

const PostCard = ({ post, actions }) => {
  const s = STATUS_META[post.status] || STATUS_META.draft;
  const name = authorName(post);
  // Live post with an edit sitting in the moderation queue.
  const editPending = post.status === "published" && !!post.pendingSubmittedAt;
  // A post an admin pulled off the blog comes back as a plain draft, so it needs
  // its own line — otherwise the author sees it silently leave the site
  // (QA 2026-07-28).
  const takenOffline = post.status === "draft" && !!post.unpublishedAt;
  // Rejection reason has to be visible where the author looks for it.
  const rejection =
    post.reviewComment &&
    (post.status === "rejected" ||
      takenOffline ||
      (post.status === "published" && post.pendingChanges))
      ? post.reviewComment
      : null;
  const cover = post.pendingChanges?.coverImage || post.coverImage;
  return (
    <Box sx={{ border: "1px solid #EEE", borderRadius: 2, p: 2, mb: 2 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "start", gap: 2 }}>
        {cover && (
          <Box
            component="img"
            src={cover}
            alt=""
            sx={{ width: 96, height: 72, objectFit: "cover", borderRadius: 1, flexShrink: 0 }}
          />
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 600, wordBreak: "break-word" }}>
            {post.title}{" "}
            <Chip size="small" label={s.label} sx={{ color: s.color, bgcolor: s.bg }} />
            {editPending && (
              <Chip
                size="small"
                label="Edited · awaiting approval"
                sx={{ ml: 0.5, color: STATUS_META.pending.color, bgcolor: STATUS_META.pending.bg }}
              />
            )}
            {takenOffline && (
              <Chip
                size="small"
                label="Taken off the blog"
                sx={{ ml: 0.5, color: STATUS_META.rejected.color, bgcolor: STATUS_META.rejected.bg }}
              />
            )}
          </Typography>
          {rejection && (
            <Typography variant="caption" sx={{ display: "block", color: "#B23A48", mt: 0.5 }}>
              Reviewer: {rejection}
            </Typography>
          )}
          {/* A pasted excerpt can be thousands of words — clamp it so one post
              cannot own the whole list (QA 2026-07-27). */}
          <Typography variant="body2" sx={{
            color: "#666", mt: 0.5, display: "-webkit-box", WebkitBoxOrient: "vertical",
            WebkitLineClamp: 3, overflow: "hidden", wordBreak: "break-word",
          }}>{post.excerpt}</Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1, flexWrap: "wrap" }}>
            <Avatar
              src={post.authorType === "nirva" ? undefined : post.author?.avatar || undefined}
              sx={{ width: 22, height: 22, fontSize: 11, bgcolor: "#576874" }}
            >
              {name?.[0] || "N"}
            </Avatar>
            <Typography variant="caption" sx={{ color: "#666" }}>{name}</Typography>
            <Typography variant="caption" sx={{ color: "#888" }}>
              · {post.category || "—"} · {postDate(post)} · {post.readTime || 1} min read
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 0.5, mt: 1, flexWrap: "wrap" }}>
            {(post.tags || []).map((t) => <Chip key={t} size="small" label={t} />)}
          </Box>
          {/* A live post should be one click away from the list. */}
          {post.status === "published" && post.slug && (
            <Link
              href={blogPostUrl(post.slug)}
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
              sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, mt: 1, fontSize: 13 }}
            >
              View on the blog
              <OpenInNewIcon sx={{ fontSize: 14 }} />
            </Link>
          )}
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>{actions}</Box>
      </Box>
    </Box>
  );
};

export default PostCard;
